import {
  onDetailsSelectionChanged,
  onMasterSelectionChange,
} from 'components/shared/data/DataGrid/handlers/selectionChanged';
import { usePagination } from 'components/shared/data/DataGrid/hooks/usePagination';
import { headerRenderers } from 'components/shared/data/DataGrid/renderers/header';
import { statusPanels } from 'components/shared/data/DataGrid/StatusPanel';
import { getHeaderMaxLevel } from 'components/shared/data/DataGrid/utils/getHeaderMaxLevel';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { useLocation } from 'react-router-dom';
import { AgGridReactFixedProp, DataGridProps } from './DataGrid.types';
import { GridReadyEvent, GridApi } from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { useSidebarOpenEffect } from 'components/layout/panel/Sidebar/useSidebarOpenEffect';
import { useResolutionChange, useTranslator, useUnmountableCallback } from 'components/shared/hooks';
import { useModalClosedContext } from 'context/ModalClosedContext';
import { useFocusCell, useGridState, useColumnConfigurator, useGridComposer } from './hooks';
import { useStyles } from './DataGrid.styles';
import { LoadingOverlay, NoRowsOverlay } from './Overlay';
import { Tooltip } from './Tooltip/Tooltip';
import { getGridSize, getTabKeyHandler } from './utils';
import { cellRenderers } from 'components/shared/data/DataGrid/renderers/cell';
import { useGridEvent } from 'components/shared/data/DataGrid/hooks';
import { fillGridBlankSpace } from 'components/shared/data/DataGrid/utils/columnSizeHelpers';
import { isUndefined } from 'lodash';

// Register AG Grid Enterprise modules
ModuleRegistry.registerModules([AllEnterpriseModule]);

export enum GridSize {
  detailsGridSpacing = 10,
  headerHeight = 58,
  rowHeight = 48,
}

// eslint-disable-next-line complexity
export const DataGrid = <T extends object, D extends object = never>(props: DataGridProps<T, D>): ReactElement => {
  const { onGridReady, onFocusBeforeGrid, onFocusAfterGrid, disableAutoSize = false, domLayout = 'normal' } = props;
  const trans = useTranslator();

  const mounted = useRef(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const { key: locationKey } = useLocation();
  const { refreshTrigger } = useModalClosedContext();
  const { composeGrid, gridVisible } = useGridComposer(gridApi, disableAutoSize);
  const effectiveRefreshKey = props.refreshKey ?? refreshTrigger;

  const classes = useStyles({
    visible: gridVisible,
    hasStatusPanel: !!props.statusPanels,
    detailsSpacing: GridSize.detailsGridSpacing,
    domLayout: domLayout,
    hasRowSelection: !!props.rowSelection && props.rowSelectionCheckboxes !== false,
  });

  const { multiColumnConfigurator, singleColumnConfigurator } = useColumnConfigurator(props.defaultColumn);

  const components = {
    gridTooltip: Tooltip,
    loadingOverlay: LoadingOverlay,
    noRowsOverlay: NoRowsOverlay,
    ...cellRenderers,
    ...headerRenderers,
    ...statusPanels,
  };

  useResolutionChange(composeGrid);
  useSidebarOpenEffect(composeGrid);
  useGridState(gridApi, props.isLoading, gridVisible);
  const { resetPagination } = usePagination(
    gridApi,
    props.onDataFetch,
    props.onDataProvide,
    {
      limit: props.limit,
      total: props.total,
    },
    props.fetchedItemsCount,
  );

  const gridSize = getGridSize(gridApi);
  const tabKeyHandler = getTabKeyHandler(gridSize, onFocusBeforeGrid, onFocusAfterGrid);
  const focusCell = useFocusCell(gridApi);

  const gridReadyHandler = useUnmountableCallback((event: GridReadyEvent) => {
    if (mounted.current) {
      setGridApi(event.api);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (onGridReady && gridApi) {
      // Initial composeGrid is done in onFirstDataRendered so column sizes match content and grid stays hidden until then.
      onGridReady({
        focusCell,
        agGridApi: gridApi,
        fitCellsToContent: composeGrid,
        agColumnApi: gridApi, // columnApi methods are now on GridApi
        resetPagination,
      });
    }
  }, [composeGrid, gridApi, focusCell, onGridReady, locationKey, props.columns.length, resetPagination]);

  useGridEvent(gridApi, 'dragStopped', ({ api }: { api: GridApi }) => {
    fillGridBlankSpace(api);
  });

  // When first data is rendered, recompute column sizes once (grid is hidden during composeGrid so no jump).
  const firstDataRenderedRef = useRef(false);
  const onFirstDataRenderedInternal = useUnmountableCallback((event: any) => {
    if (!firstDataRenderedRef.current) {
      firstDataRenderedRef.current = true;
      composeGrid();
    }
    props.onFirstDataRendered?.(event);
  }, [composeGrid, props.onFirstDataRendered]);

  // Refresh grid when data or refreshKey/context changes (e.g. after closing a modal with cancel) so layout/colSpan renders correctly.
  // Only refreshCells - do not call composeGrid() here to avoid visible column rescale/jump for the user.
  useEffect(() => {
    if (!gridApi || props.onDataProvide) return;
    gridApi.refreshCells({ force: true });
  }, [gridApi, props.data, effectiveRefreshKey, props.onDataProvide]);

  const gridCommonOptions: AgGridReactFixedProp = {
    theme: themeQuartz,
    enableCellTextSelection: true,
    icons: props.icons,
    suppressContextMenu: !props.contextMenu,
    suppressHeaderContextMenu: true,
    suppressColumnMenu: true,
    groupDefaultExpanded: undefined,
    rowClass: [props.rowClassName],
    getRowClass: props.getRowClass,
    rowClassRules: props.rowClassRules,
    rowSelection: props.rowSelection
      ? {
          mode: props.rowSelection === 'multiple' ? 'multiRow' : 'singleRow',
          checkboxes: props.rowSelection === 'multiple' && props.rowSelectionCheckboxes !== false,
          headerCheckbox: props.rowSelection === 'multiple' && props.rowSelectionCheckboxes !== false,
          // When using custom checkbox cells, disable row-body click selection so that only
          // the checkbox column drives selection (otherwise clicking anywhere on the row
          // triggers single-select behaviour, overriding the checkbox).
          enableClickSelection: props.rowSelectionCheckboxes !== false,
        }
      : undefined,
    suppressRowClickSelection: false,
    groupDisplayType: props.groupMultiAutoColumn ? 'multipleColumns' : (props.groupSuppressAutoColumn ? 'custom' : 'singleColumn'),
    components: components,
    loadingCellRenderer: 'loadingRecords',
    loadingOverlayComponent: 'loadingOverlay',
    noRowsOverlayComponent: 'noRowsOverlay',
    keepDetailRows: true,
    keepDetailRowsCount: Infinity,
    tabToNextCell: tabKeyHandler,
    getRowHeight: (props.getRowHeight
      ? props.getRowHeight
      : (params: any) => {
          if (params.node.detail && !props.masterDetail) {
            const headerLevels = getHeaderMaxLevel(props.detailsColumns);
            const rowsCount = props.detailsMapping(params.data).length;

            return (
              headerLevels * GridSize.headerHeight + 2 * GridSize.detailsGridSpacing + rowsCount * GridSize.rowHeight
            );
          } else if (params.node.detail && props.masterDetail) {
            return props.detailRowHeight;
          } else {
            return GridSize.rowHeight;
          }
        }) as any,
    loadingOverlayComponentParams: {
      loadingMessage: trans(props.loadingMessage || 'COMMON.LOADING_DATA'),
    },
    noRowsOverlayComponentParams: {
      noResultsMessage: trans(props.noResultsMessage || 'COMMON.NOTHING-FOUND'),
    },
    headerHeight: props.headerHeight,
    scrollbarWidth: 8,
    defaultColDef: {
      suppressHeaderMenuButton: true,
    },
    localeText: {
      selectAll: trans('COMMON.GRID.SELECT_ALL'),
      searchOoo: trans('COMMON.GRID.SEARCH'),
      blanks: trans('COMMON.GRID.BLANK'),
      pinColumn: trans('COMMON.GRID.PIN_COLUMN'),
      pinColumnLeft: trans('COMMON.GRID.PIN_LEFT'),
      pinColumnRight: trans('COMMON.GRID.PIN_RIGHT'),
      noPin: trans('COMMON.GRID.NO_PIN'),
      autosizeThisColumn: trans('COMMON.GRID.AUTOSIZE_THIS_COLUMN'),
      autosizeAllColumns: trans('COMMON.GRID.AUTOSIZE_ALL_COLUMNS'),
      resetColumns: trans('COMMON.GRID.RESET_COLUMNS'),
      expandAll: trans('COMMON.GRID.EXPAND_ALL'),
      collapseAll: trans('COMMON.GRID.COLLAPSE_ALL'),
    },
    getRowId: props.getRowId ? (params) => props.getRowId(params.data) : undefined,
  };

  const getGroupDefaultExpanded = () => {
    if (props.onDataProvide) return undefined;
    return isUndefined(props.defaultExpand) ? -1 : props.defaultExpand;
  };

  return (
    <div
      className={classNames(classes.container, props.className)}
      data-testid="dataGrid-container"
    >
      <AgGridReact
        {...gridCommonOptions}
        tooltipShowDelay={500}
        onGridReady={gridReadyHandler}
        onFirstDataRendered={onFirstDataRenderedInternal}
        treeData={!!props.treeHierarchy}
        getDataPath={props.treeHierarchy}
        columnDefs={multiColumnConfigurator(props.columns)}
        autoGroupColumnDef={singleColumnConfigurator(props.groupingColumn)}
        groupDefaultExpanded={getGroupDefaultExpanded()}
        suppressScrollOnNewData={props.suppressScrollOnNewData}
        suppressColumnVirtualisation={props.suppressColumnVirtualisation}
        statusBar={{ statusPanels: props.statusPanels || [] }}
        rowData={props.data}
        pinnedBottomRowData={props.pinnedBottomData}
        rowModelType={props.onDataProvide ? 'serverSide' : 'clientSide'}
        masterDetail={!!props.detailsColumns || props.masterDetail}
        isRowMaster={props.isRowMaster}
        cacheBlockSize={props.limit || 100}
        isRowSelectable={props.isRowSelectable ? (node) => props.isRowSelectable(node.data, node) : undefined}
        onSelectionChanged={onMasterSelectionChange(props)}
        detailCellRenderer={props.detailCellRenderer}
        detailRowHeight={props.detailRowHeight}
        detailCellRendererParams={
          props.detailsColumns && {
            detailGridOptions: {
              ...gridCommonOptions,
              columnDefs: multiColumnConfigurator(props.detailsColumns),
              onSelectionChanged: onDetailsSelectionChanged(props, gridApi),
              isRowSelectable: props.isDetailsRowSelectable
                ? (node) => props.isDetailsRowSelectable(node.data, node)
                : undefined,
            },
            getDetailRowData: (params) => {
              params.successCallback(props.detailsMapping(params.data));
            },
          }
        }
        suppressAggFuncInHeader={props.suppressAggFuncInHeader}
        context={{
          ...props.context,
          detailsMapping: props.detailsMapping,
        }}
      />
    </div>
  );
};
